/**
 * Reassign Drawer
 *
 * Fast, frictionless reassignment within the SAME assignment group.
 * For moving tickets to a DIFFERENT team, use Escalation instead.
 *
 * Design principles:
 * - Quick selection of team members
 * - No confirmation required
 * - Optional note (not mandatory)
 * - Only shows techs in the same assignment group
 */

import { useState, useMemo } from 'react';
import type { Ticket, Tech, AssignmentGroup } from '../../types/ticket';
import { AppButton, AppDrawer, Stack } from '../ui';
import './ReassignDrawer.css';

interface ReassignDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket | null;
  technicians: Tech[];
  onReassign: (ticketId: string, technicianId: string, comment: string, priority?: string) => void;
  onError?: (message: string) => void;
}

export const ReassignDrawer: React.FC<ReassignDrawerProps> = ({
  isOpen,
  onClose,
  ticket,
  technicians,
  onReassign,
  onError,
}) => {
  const [selectedTechId, setSelectedTechId] = useState<string>('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter techs to only show those in the same assignment group
  const teamMembers = useMemo(() => {
    if (!ticket?.assignmentGroup) {
      // If ticket has no group, show all available techs
      return technicians.filter((t) => t.isAvailable);
    }
    return technicians.filter(
      (t) => t.assignmentGroup === ticket.assignmentGroup && t.isAvailable
    );
  }, [technicians, ticket?.assignmentGroup]);

  // Get current assignee
  const currentTech = useMemo(() => {
    if (!ticket?.assignedTechId) return null;
    return technicians.find((t) => t.id === ticket.assignedTechId) ?? null;
  }, [technicians, ticket?.assignedTechId]);

  // Filter out current assignee from the list
  const availableTeamMembers = useMemo(() => {
    return teamMembers.filter((t) => t.id !== ticket?.assignedTechId);
  }, [teamMembers, ticket?.assignedTechId]);

  const handleQuickReassign = async (techId: string) => {
    if (!ticket) return;

    setSelectedTechId(techId);
    setIsSubmitting(true);

    try {
      // Quick reassignment - no note required
      await onReassign(ticket.ticketNumber, techId, note.trim() || 'Reassigned within team');
      handleClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reassignment failed';
      onError?.(message);
      setIsSubmitting(false);
      setSelectedTechId('');
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedTechId('');
      setNote('');
      onClose();
    }
  };

  if (!ticket) return null;

  const groupName = ticket.assignmentGroup ?? 'Service Desk';

  return (
    <AppDrawer
      open={isOpen}
      onClose={handleClose}
      title="Reassign Ticket"
      subtitle={`Select a ${groupName} team member`}
      width={420}
      loading={isSubmitting}
    >
      <div className="reassign-drawer__content">
        {/* Ticket Summary */}
        <div className="reassign-drawer__ticket-summary">
          <span className="reassign-drawer__ticket-number">{ticket.ticketNumber}</span>
          <p className="reassign-drawer__ticket-desc">{ticket.shortDescription}</p>
          {currentTech && (
            <div className="reassign-drawer__current">
              <span className="reassign-drawer__current-label">Currently assigned to</span>
              <span className="reassign-drawer__current-name">{currentTech.name}</span>
            </div>
          )}
        </div>

        {/* Team Members Grid */}
        <div className="reassign-drawer__section">
          <h3 className="reassign-drawer__section-title">
            {groupName} Team
            <span className="reassign-drawer__section-count">
              {availableTeamMembers.length} available
            </span>
          </h3>

          {availableTeamMembers.length === 0 ? (
            <div className="reassign-drawer__empty">
              <p>No other team members available in {groupName}.</p>
              <p className="reassign-drawer__empty-hint">
                Use Escalation to transfer to a different team.
              </p>
            </div>
          ) : (
            <div className="reassign-drawer__tech-grid">
              {availableTeamMembers.map((tech) => {
                const isSelected = selectedTechId === tech.id;
                const loadPercentage = (tech.currentLoad / tech.maxLoad) * 100;
                const isHighLoad = loadPercentage >= 80;

                return (
                  <button
                    key={tech.id}
                    type="button"
                    className={`reassign-drawer__tech-card ${isSelected ? 'reassign-drawer__tech-card--selected' : ''}`}
                    onClick={() => handleQuickReassign(tech.id)}
                    disabled={isSubmitting}
                  >
                    <div className="reassign-drawer__tech-avatar">
                      {tech.name.charAt(0)}
                    </div>
                    <div className="reassign-drawer__tech-info">
                      <span className="reassign-drawer__tech-name">{tech.name}</span>
                      <span className={`reassign-drawer__tech-load ${isHighLoad ? 'reassign-drawer__tech-load--high' : ''}`}>
                        {tech.currentLoad}/{tech.maxLoad} tickets
                      </span>
                    </div>
                    {isSelected && isSubmitting && (
                      <div className="reassign-drawer__tech-spinner" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Optional Note */}
        <div className="reassign-drawer__note-section">
          <label htmlFor="reassign-note" className="reassign-drawer__note-label">
            Note <span className="reassign-drawer__optional">(optional)</span>
          </label>
          <input
            id="reassign-note"
            type="text"
            className="reassign-drawer__note-input"
            placeholder="Brief reason for reassignment..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            disabled={isSubmitting}
          />
        </div>

        {/* Footer */}
        <div className="reassign-drawer__footer">
          <AppButton variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </AppButton>
        </div>
      </div>
    </AppDrawer>
  );
};
